/**
 * Safe storage utilities with JSON formatting and error handling
 */

export interface StorageOptions {
  fallback?: any;
  silent?: boolean;
}

/**
 * Safely get data from localStorage with JSON parsing
 */
export const getStorageItem = <T = any>(
  key: string, 
  options: StorageOptions = {}
): T | null => {
  const { fallback = null, silent = true } = options;
  
  try {
    if (typeof window === 'undefined') {
      return fallback;
    }
    
    const item = localStorage.getItem(key);
    if (item === null) {
      return fallback;
    }
    
    return JSON.parse(item) as T;
  } catch (error) {
    if (!silent) {
      console.error(`Error reading from localStorage key "${key}":`, error);
    }
    return fallback;
  }
};

/**
 * Safely set data in localStorage with JSON stringification
 */
export const setStorageItem = (
  key: string, 
  value: any, 
  options: StorageOptions = {}
): boolean => {
  const { silent = true } = options;
  
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
    return true;
  } catch (error) {
    if (!silent) {
      console.error(`Error writing to localStorage key "${key}":`, error);
    }
    return false;
  }
};

/**
 * Safely remove data from localStorage
 */
export const removeStorageItem = (
  key: string, 
  options: StorageOptions = {}
): boolean => {
  const { silent = true } = options;
  
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    if (!silent) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
    return false;
  }
};

/**
 * Check if localStorage is available
 */
export const isStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Clear all localStorage data (use with caution)
 */
export const clearStorage = (options: StorageOptions = {}): boolean => {
  const { silent = true } = options;
  
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    
    localStorage.clear();
    return true;
  } catch (error) {
    if (!silent) {
      console.error('Error clearing localStorage:', error);
    }
    return false;
  }
};